import type {
    IDependencyCondition,
    IDependencyRule,
    IModuleField,
} from '@/features/module-builder/types';

/**
 * Pure evaluation of field dependency rules against the current form values
 * (keyed by field NAME). Mirrors the backend `field-dependency.util.ts` —
 * keep semantics in sync:
 * - `equals` on a MULTI_SELECT controller means array-contains.
 * - A rule with zero conditions is treated as not set (always true).
 * - Missing/inactive controlling fields make their condition false.
 */

export interface DependencyState {
    /** Field NAMES that are currently hidden. */
    hidden: Set<string>;
    /** Field NAMES that are currently (effectively) required. */
    required: Set<string>;
}

function isSet(value: unknown): boolean {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
}

function matchesValue(formValue: unknown, expected: unknown): boolean {
    if (Array.isArray(formValue)) {
        return formValue.some((item) => String(item) === String(expected));
    }
    if (typeof formValue === 'boolean' || typeof expected === 'boolean') {
        const toBool = (v: unknown): boolean => v === true || v === 'true';
        return toBool(formValue) === toBool(expected);
    }
    return String(formValue) === String(expected);
}

function evaluateCondition(
    condition: IDependencyCondition,
    values: Record<string, unknown>,
    fieldsById: Map<string, IModuleField>,
): boolean {
    const controller = fieldsById.get(condition.fieldId);
    if (!controller || !controller.isActive) return false;
    const value = values[controller.name];

    switch (condition.operator) {
        case 'is_set':
            return isSet(value);
        case 'is_not_set':
            return !isSet(value);
        case 'equals':
            return isSet(value) && matchesValue(value, condition.value);
        case 'not_equals':
            return !isSet(value) || !matchesValue(value, condition.value);
        case 'in': {
            const expected = Array.isArray(condition.value) ? condition.value : [condition.value];
            return isSet(value) && expected.some((v) => matchesValue(value, v));
        }
        case 'not_in': {
            const expected = Array.isArray(condition.value) ? condition.value : [condition.value];
            return !isSet(value) || !expected.some((v) => matchesValue(value, v));
        }
        default:
            return false;
    }
}

export function evaluateRule(
    rule: IDependencyRule | undefined,
    values: Record<string, unknown>,
    fieldsById: Map<string, IModuleField>,
): boolean {
    if (!rule || !Array.isArray(rule.conditions) || rule.conditions.length === 0) return true;
    const results = rule.conditions.map((c) => evaluateCondition(c, values, fieldsById));
    return rule.logic === 'OR' ? results.some(Boolean) : results.every(Boolean);
}

/** Single accessor for a field's dependency rules (isolates the storage shape). */
export function getFieldDependencies(field: IModuleField) {
    return field.dependencyRules ?? null;
}

/** Field NAMES the record form should watch (controllers of any rule). */
export function collectControllerNames(fields: IModuleField[]): string[] {
    const fieldsById = new Map(fields.map((f) => [f.id, f]));
    const names = new Set<string>();
    for (const field of fields) {
        const rules = getFieldDependencies(field);
        if (!rules) continue;
        for (const rule of [rules.visibleWhen, rules.requiredWhen]) {
            for (const condition of rule?.conditions ?? []) {
                const controller = fieldsById.get(condition.fieldId);
                if (controller) names.add(controller.name);
            }
        }
    }
    return [...names];
}

/** Evaluate visibility + effective requiredness for every field. */
export function evaluateDependencies(
    fields: IModuleField[],
    values: Record<string, unknown>,
): DependencyState {
    const fieldsById = new Map(fields.map((f) => [f.id, f]));
    const hidden = new Set<string>();
    const required = new Set<string>();

    for (const field of fields) {
        const rules = getFieldDependencies(field);
        const visible = evaluateRule(rules?.visibleWhen, values, fieldsById);
        if (!visible) {
            hidden.add(field.name);
            continue; // Hidden fields are never required.
        }
        if (field.isRequired) {
            required.add(field.name);
        } else if (rules?.requiredWhen && evaluateRule(rules.requiredWhen, values, fieldsById)) {
            required.add(field.name);
        }
    }
    return { hidden, required };
}
